import { Users, MoreVertical } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { memberState, type TeamMember } from '../types';

interface TeamAttendanceProps {
  team: TeamMember[];
  loading: boolean;
  isError: boolean;
}

const AVATAR_COLORS = [
  'bg-[#8B5CF6] text-white',
  'bg-[#10B981] text-white',
  'bg-[#F59E0B] text-white',
  'bg-[#3B82F6] text-white',
  'bg-[#EC4899] text-white',
  'bg-[#6366F1] text-white',
];

function getInitials(name: string) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

export function TeamAttendance({ team, loading, isError }: TeamAttendanceProps) {
  const columns: Column<TeamMember>[] = [
    {
      key: 'name',
      header: 'EMPLOYEE',
      className: 'py-3.5 pl-6',
      render: (m) => {
        const initials = getInitials(m.display_name);
        const colorClass = getAvatarColor(m.display_name || m.id);
        return (
          <div className="flex items-center gap-3">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${colorClass}`}
            >
              {initials}
            </div>
            <span className="text-sm font-semibold text-[#11183F]">{m.display_name}</span>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'STATUS',
      className: 'py-3.5',
      render: (m) => {
        const s = memberState(m);
        if (s === 'present') {
          return (
            <span className="inline-flex items-center rounded-full bg-[#D1FAE5] px-3.5 py-1 text-xs font-semibold text-[#059669]">
              Checked In
            </span>
          );
        }
        if (s === 'checked_out') {
          return (
            <span className="inline-flex items-center rounded-full bg-[#DBEAFE] px-3.5 py-1 text-xs font-semibold text-[#2563EB]">
              Checked Out
            </span>
          );
        }
        return (
          <span className="inline-flex items-center rounded-full bg-[#EEF2F6] px-3.5 py-1 text-xs font-semibold text-[#64748B]">
            Not In
          </span>
        );
      },
    },
    {
      key: 'check_in',
      header: 'CHECK IN',
      className: 'py-3.5 text-sm text-[#475569]',
      render: (m) => m.check_in || '—',
    },
    {
      key: 'check_out',
      header: 'CHECK OUT',
      className: 'py-3.5 text-sm text-[#475569]',
      render: (m) => m.check_out || '—',
    },
    {
      key: 'hours',
      header: 'HOURS',
      className: 'py-3.5 text-sm text-[#475569]',
      render: (m) => (m.hours != null && m.hours > 0 ? `${m.hours}h` : '—'),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      className: 'py-3.5 pr-6',
      render: () => (
        <div className="flex justify-end">
          <MoreVertical className="h-4 w-4 text-[#94A3B8] hover:text-[#475569] cursor-pointer transition-colors" />
        </div>
      ),
    },
  ];

  return (
    <Card className="rounded-2xl border border-[#E8E8F5] bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <Users className="h-6 w-6 text-[#6825F5]" />
        <h2 className="text-lg font-bold tracking-tight text-[#11183F]">Team Attendance — Today</h2>
      </div>
      <div>
        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-body-sm text-on-surface-variant">Failed to load team attendance.</p>
        ) : (
          <Table
            bare
            columns={columns}
            data={team}
            rowKey={(m) => m.id}
            empty="No team members found for your department."
            className="rounded-2xl overflow-hidden border border-[#F1F3F9] [&_thead]:bg-[#F8F9FE] [&_th]:text-[#747C9C] [&_th]:font-bold [&_th]:text-[11px] [&_th]:tracking-wider [&_th]:py-3.5 [&_tbody_tr]:border-b [&_tbody_tr]:border-[#F1F3F9] [&_tbody_tr:hover]:bg-[#F8F9FE]/60 [&_tbody_tr:last-child]:border-none"
          />
        )}
      </div>
    </Card>
  );
}
